import { useSchool, API, logout } from '@sportschool/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Loader2, Sparkles, Check, ChevronRight, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface Props {
  schoolId: string;
}

export function JoinSchoolView({ schoolId }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: school, isLoading: schoolLoading } = useSchool(schoolId);
  const [joining, setJoining] = useState(false);

  const joinMutation = API.useSelfJoinSchool({
    mutation: {
      onSuccess: async () => {
        // Clear TanStack query caches to pull fresh permissions context
        await queryClient.clear();

        // If the current URL has a specific sub-path (like classes, schedules, etc.),
        // redirect back to the current URL so they stay on the target page.
        // Otherwise, fallback to the default student portal.
        const currentPath = window.location.pathname + window.location.search;
        const isGenericJoin = currentPath.includes('/join/');
        
        if (!isGenericJoin && currentPath !== '/' && currentPath !== `/schools/${schoolId}`) {
          navigate(currentPath, { replace: true });
        } else {
          navigate(`/schools/${schoolId}/student`, { replace: true });
        }
      },
      onError: () => {
        setJoining(false);
      }
    }
  });

  const handleJoin = async () => {
    setJoining(true);
    joinMutation.mutate({ schoolId });
  };

  if (schoolLoading) {
    return <LoadingSpinner fullScreen />;
  }

  const schoolName = school?.name || 'SportSchool School';
  const sportType = school?.sportType || 'Sports';
  const logoUrl = school?.logoUrl;
  const alias = (school as { alias?: string | null } | null)?.alias || '';

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-4 relative overflow-hidden">
      {/* Background Harmonious Gradients */}
      <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 via-transparent to-primary/5 pointer-events-none" />
      <div className="absolute -top-[40%] -right-[30%] w-[80%] h-[80%] rounded-full bg-primary/5 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-[40%] -left-[30%] w-[80%] h-[80%] rounded-full bg-primary/5 blur-[120px] pointer-events-none" />

      <div className="w-full max-w-lg z-10 flex flex-col items-center gap-6">
        {/* Brand Header */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
            <Sparkles className="size-4" />
          </div>
          <span className="font-bold text-sm tracking-wide text-foreground uppercase">
            {t('join.platform')}
          </span>
        </div>

        {/* Presentation Card */}
        <Card className="w-full border-muted-foreground/15 bg-card/65 backdrop-blur-md rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300">
          <CardContent className="p-8 flex flex-col items-center text-center gap-6">
            
            {/* School Logo */}
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={schoolName}
                className="max-h-20 max-w-[240px] w-auto object-contain hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="flex size-20 items-center justify-center rounded-2xl bg-primary text-primary-foreground font-extrabold text-2xl shadow-md">
                {schoolName.substring(0, 2).toUpperCase()}
              </div>
            )}

            {/* School Information */}
            <div className="flex flex-col gap-1.5 w-full">
              <h1 className="text-2xl font-extrabold text-foreground tracking-tight line-clamp-2 px-4">
                {schoolName}
              </h1>
              <div className="flex items-center justify-center gap-2 mt-1">
                <span className="text-xs font-medium bg-primary/10 text-primary px-2.5 py-0.5 rounded-full capitalize">
                  {sportType.toLowerCase()} {t('join.club')}
                </span>
                {alias && (
                  <span className="text-xs font-semibold font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">
                    @{alias}
                  </span>
                )}
              </div>
            </div>

            <hr className="w-full border-muted-foreground/10" />

            {/* Welcome pitch */}
            <div className="flex flex-col gap-2.5 px-2">
              <h3 className="text-base font-bold text-foreground">
                {t('join.title')}
              </h3>
              <p className="text-xs leading-relaxed text-muted-foreground/90 max-w-sm">
                {t('join.description')}
              </p>
            </div>

            {/* Value Highlights */}
            <div className="grid grid-cols-2 gap-3 w-full mt-2">
              {[
                t('join.benefit1'),
                t('join.benefit2'),
                t('join.benefit3'),
                t('join.benefit4'),
              ].map((text, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 p-2.5 rounded-xl bg-muted/40 border border-muted-foreground/5 hover:bg-muted/70 transition-colors duration-150 text-left"
                >
                  <div className="flex size-4 items-center justify-center rounded-full bg-green-500/10 text-success shrink-0">
                    <Check className="size-2.5" />
                  </div>
                  <span className="text-[11px] font-medium text-foreground/90 leading-tight">
                    {text}
                  </span>
                </div>
              ))}
            </div>

            {/* Action Button */}
            <Button
              onClick={handleJoin}
              disabled={joining}
              className="w-full h-11 mt-4 text-xs font-bold gap-2 rounded-xl shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-200"
            >
              {joining ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t('join.joining')}
                </>
              ) : (
                <>
                  <UserPlus className="size-4" />
                  {t('join.button')}
                  <ChevronRight className="size-4 shrink-0" />
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Navigation & Account Controls */}
        <div className="flex items-center gap-4 mt-2">
          <button
            onClick={() => navigate('/select-school')}
            className="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline transition-colors"
          >
            ← {t('join.back')}
          </button>
          <span className="text-muted-foreground/35 text-[10px] pointer-events-none select-none">|</span>
          <button
            onClick={() => logout()}
            className="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline transition-colors"
          >
            {t('join.changeAccount')}
          </button>
        </div>
      </div>
    </div>
  );
}
